import React from "react";
import FileUploader from "./FIleUploader";
import { checkFinanceOption, isCrypto } from "constants/global";
import QuestionHelpIcon from "../QuestionHelpIcon";
import { RadioGroup, RadioGroupItem } from "../ui/Radio";
import {
  fileTypeToUpload,
  MINIMUM_AMOUNT,
  getOrderToDisplay,
  radioFinanceOptions,
  MAXIMUM_AMOUNT,
  CRYPTO_MINIMUM_AMOUNT,
} from "constants/FinanceConstants";
import { DeleteIcon, InvoiceIcon, ProfileFilledIcon, UserIcon } from "asset/icons";
import { currencyToDisplay, formatDate } from "../flex/utils";
import PurchaseInvoiceSelect from "../flex/shipments/components/modals/PurchaseInvoiceSelect";
import capitalizeWords from "utilities/capitalizeWords";
import PurchaseOrdersSelect from "../flex/shipments/components/PurchaseOrderSelect";
import CurrencyToggle from "../currencyToggle";
import { SearchInput as InputField } from "../TableSearchInput";
import { Loader } from "lucide-react";
import { unformat } from "@react-input/number-format";

const ReceivableFinanceForm = ({
  paymentMethod,
  selectedOption,
  setSelectedOption,
  orderDimensionsList,
  handleDelete,
  setOrderDimensionsList,
  setFileKey,
  setAddDocModal,
  setRequestedAmount,
  isAmountValid,
  requestedAmount,
  maxAmount,
  transactionDocument,
  setTransactionDocument,
  freightDocument,
  setFreightDocument,
  contractAgreement,
  setContractAgreement,
  businessId,
  selectedUser,
  isLoading,
  newWalletsData,
  isLoadingNewWallets,
  refetchNewWallets, currency,
  trnxAndFreightSelected
}) => {
  const supplier = getOrderToDisplay({ isPayableFinance: paymentMethod === checkFinanceOption("Payables finance"), businessId, data: selectedUser });

  const MINIMUM_AMOUNT_TO_USE = isCrypto(currency) ? CRYPTO_MINIMUM_AMOUNT : MINIMUM_AMOUNT;

  const getAmountValidationMessage = () => {
    const amount = unformat(requestedAmount);
    if (amount > MAXIMUM_AMOUNT) {
      return `This amount is greater than the maximum amount - ${currencyToDisplay(currency, MAXIMUM_AMOUNT)}`;
    }
    if (!trnxAndFreightSelected && amount > Number(maxAmount)) {
      return `This amount is greater than the maximum - ${currencyToDisplay(currency, maxAmount)}`;
    }
    return `This amount is less than the minimum - ${currencyToDisplay(currency, MINIMUM_AMOUNT_TO_USE)}`;
  };

  return (
    <>
      {paymentMethod === checkFinanceOption("Payables finance") && (
        <div>
          <label className="mb-1 text-sm text-gray-70">
            Finance Category{" "}
            <QuestionHelpIcon
              details={
                <span>
                  Get finance for your purchase orders, transactions and trips
                </span>
              }
            />
          </label>
          <RadioGroup
            className="flex items-center justify-start gap-4"
            defaultValue={selectedOption}
            value={selectedOption}
            onValueChange={(value) => {
              setRequestedAmount("")
              setSelectedOption(value)
            }}
          >
            {radioFinanceOptions.map((option) => (
              <div className="flex items-center gap-1" key={option.value}>
                <RadioGroupItem value={option.value} id={option.value} />
                <label htmlFor={option.value} className="text-sm text-gray-70">
                  {option.label}
                </label>
              </div>
            ))}
          </RadioGroup>
        </div>
      )}
      {selectedOption === radioFinanceOptions[0]?.value && (
        <>
          <div className="">
            <label className="mb-1 text-sm text-gray-70">
              {paymentMethod === checkFinanceOption("Receivables finance")
                ? "What invoice is this for?"
                : "What purchase order is this for?"}
            </label>
            {orderDimensionsList?.length ? (
              orderDimensionsList.map((item, index) => {
                return (
                  <div key={index} className="flex items-center gap-x-3">
                    <div className="flex justify-between w-full p-3 mb-2 bg-white rounded-lg">
                      <div className="flex items-center gap-x-2">
                        <div className="flex items-center justify-center flex-shrink-0 p-2 bg-purple-100 rounded-full">
                          <InvoiceIcon className="size-4" />
                        </div>
                        <div className="flex flex-col">
                          <p className="">{item?.merchant}</p>
                          <div className="flex items-center text-xs gap-1 text-[14px] text-[#6B7280]">
                            <span>{item?.title}</span>
                            <span>•</span>
                            <span>{item?.outbound?.businessName}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <p className="text-right">
                          {item?.totalAmount
                            ? `${currencyToDisplay(item?.currency, item?.totalAmount)}`
                            : "--"}
                        </p>
                        <div className="text-right text-xs text-[#6B7280]">
                          {formatDate(item?.createdAt)}
                        </div>
                      </div>
                    </div>

                    <DeleteIcon
                      className="pointer"
                      onClick={() => handleDelete(item?._id)}
                    />
                  </div>
                );
              })
            ) : (
              <>
                {paymentMethod === checkFinanceOption("Receivables finance") ? (
                  <PurchaseInvoiceSelect
                    setOrderDimensionsList={(value) => {
                      setOrderDimensionsList([value]);
                    }}
                    forFinance={true}
                  />
                ) : (
                  <PurchaseOrdersSelect
                    setOrderDimensionsList={(value) =>
                      setOrderDimensionsList([value])
                    }
                    forFinance={true}
                  />
                )}
              </>
            )}
          </div>
          {isLoading ? (
            <Loader />
          ) : !orderDimensionsList?.length ? null : supplier ? (
            <div>
              <label className="mb-1 text-sm text-gray-70">Contract Employer</label>
              <div className="bg-white border !border-secondary-50 rounded-lg !p-4 flex items-center relative cursor-pointer">
                <input
                  required
                  defaultValue={supplier?.businessName}
                  className="absolute top-0 left-0 h-full -z-30"
                />

                  <ProfileFilledIcon className="rounded-full mr-1.5 -ml-1 -my-0.5" />
                  <span className="font-[450] mr-auto text-main-dark">
                    {capitalizeWords(supplier?.businessName?.toLowerCase())}
                  </span>
                </div>
              </div>
            ) : (
              <div>
                <label className="mb-1 text-sm text-gray-70">Contract Employer</label>

              <div className={` h-[56px] border rounded-[8px] bg-white`}>
                <div
                  className={`pl-1 duration-200 transition cursor-pointer flex w-full items-center h-full rounded-[8px]`}
                >
                  <div
                    className={`flex items-center justify-center p-2 rounded-full size-8 bg-gray-100`}
                  >
                    <UserIcon className="text-base" />
                  </div>
                  <div className="w-full flex justify-between items-center text-md pl-[8px]">
                    <p className="text-sm text-gray-70">Contract Employer</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <div>
        <label className="mb-1 text-sm text-gray-70">
          How much do you need?{" "}
          <QuestionHelpIcon
            details={
              <span>
                Amount to be requested <br /> Minimum - {currencyToDisplay(currency, MINIMUM_AMOUNT_TO_USE)} <br />
                Maximum - {currencyToDisplay(currency, MAXIMUM_AMOUNT)}
              </span>
            }
          />
        </label>
        <div className="flex items-start gap-2">
          <div className="bg-secondary-30 px-2 flex items-center justify-center rounded-md h-[56px]">
            <CurrencyToggle
              forCreditWallet
              newWalletsData={newWalletsData}
              isLoadingNewWallets={isLoadingNewWallets}
              refetchNewWallets={refetchNewWallets}
              dropDownClassName="shadow-dropdown2 !left-0 !top-[109%]"
              className="!text-gray-10 !text-base font-light gap-1 flex items-center justify-center w-[115px] !ml-2"
            />
          </div>
          <div className="w-full">
            <InputField
              onChange={(value) => {
                setRequestedAmount(value);
              }}
              isValid={!isAmountValid}
              value={requestedAmount}
              disabled={
                selectedOption === radioFinanceOptions[0]?.value
                  ? !orderDimensionsList.length
                  : false
              }
              showIcon={false}
              placeholder="Enter amount"
              wrapperClassName="max-w-full h-[56px]"
            />
            {isAmountValid && (
              <p className="text-xs text-[#C01515] font-normal">
                {getAmountValidationMessage()}
              </p>
            )}
          </div>
        </div>
      </div>
      {selectedOption === radioFinanceOptions[1]?.value && (
        <FileUploader
          title="Transaction document"
          forFinance={true}
          setFileKey={() => setFileKey("Board resolution to open an account")}
          file={transactionDocument}
          onClick={() => setAddDocModal(fileTypeToUpload.Transaction)}
          setFile={setTransactionDocument}
        />
      )}
      {selectedOption === radioFinanceOptions[2]?.value && (
        <FileUploader
          title="Freight document"
          forFinance={true}
          setFileKey={() => setFileKey("Board resolution to open an account")}
          file={freightDocument}
          onClick={() => setAddDocModal(fileTypeToUpload.Freight)}
          setFile={setFreightDocument}
        />
      )}
      <FileUploader
        title="Contract agreement"
        forFinance={true}
        details={
          <span>Agreement to carry out this contract with your contract employer</span>
        }
        setFileKey={() => setFileKey("Bank statement")}
        file={contractAgreement}
        onClick={() => setAddDocModal(fileTypeToUpload.Contract)}
        setFile={setContractAgreement}
      />
      {/* <FileUploader
                title="Domiciliation agreement"
                details={
                    <span>
                        Agreement with your contract employer to repay the loan into <br />{" "}
                        [Customer name/Tradevucommerce]’s bank account when due
                    </span>
                }
                forFinance={true}
                setFileKey={() => setFileKey("Board resolution to open an account")}
                file={domiciliationAgreement}
                onClick={() => setAddDocModal(fileTypeToUpload.Domiciliation)}
                setFile={setDomiciliationAgreement}
            /> */}
    </>
  );
};

export default ReceivableFinanceForm;
